import { ProductVariantEditor } from '@features/catalog/models/product-form-draft.models';
import { Product, ProductVariant } from '@features/catalog/models/product.models';

export function isProductVariantActive(
  variant: Pick<ProductVariant, 'active'> | Pick<ProductVariantEditor, 'active'>
): boolean {
  return variant.active !== false;
}

export function mapProductVariantToEditor(variant: ProductVariant): ProductVariantEditor {
  return {
    id: variant.id,
    isNew: false,
    name: variant.name ?? '',
    sku: variant.sku ?? '',
    externalReference: variant.externalReference ?? '',
    imageUrl: variant.imageUrl ?? null,
    imageUrls: variant.imageUrls,
    cost: null,
    price: Number(variant.price ?? 0),
    stockQuantity: variant.stockQuantity ?? null,
    lowStockThreshold: variant.lowStockThreshold ?? null,
    inventoryManaged: variant.inventoryManaged ?? false,
    inventoryItemId: variant.inventoryItemId ?? null,
    position: variant.position ?? null,
    active: variant.active ?? true,
    attributeValues: (variant.productAttributeValues ?? [])
      .map((value) => ({
        id: value.id,
        definitionId: value.productAttributeDefinition.id,
        definitionName: value.productAttributeDefinition.name,
        value: value.value,
      }))
      .filter(
        (value) =>
          value.id.trim().length > 0 &&
          value.definitionId.trim().length > 0 &&
          value.value.trim().length > 0
      ),
    attributesText: variant.attributesText ?? '',
  };
}

export function mapProductVariantsToEditors(
  variants: readonly ProductVariant[] | null | undefined
): ProductVariantEditor[] {
  return (variants ?? []).map((variant) => mapProductVariantToEditor(variant));
}

/** Soft-delete (`active: false`) no debe reaparecer en el editor ni al regenerar. */
export function mapActiveProductVariantsToEditors(
  variants: readonly ProductVariant[] | null | undefined
): ProductVariantEditor[] {
  return mapProductVariantsToEditors(variants).filter((variant) => isProductVariantActive(variant));
}

export function mergeProductVariantEditor(
  current: ProductVariantEditor,
  saved: ProductVariantEditor
): ProductVariantEditor {
  return {
    ...saved,
    imageUrl: saved.imageUrl ?? current.imageUrl,
    imageUrls: saved.imageUrls ?? current.imageUrls,
  };
}

export function mergeSavedVariantsIntoEditors(
  variants: readonly ProductVariantEditor[],
  savedVariants: readonly ProductVariant[]
): ProductVariantEditor[] {
  const savedById = new Map(
    savedVariants.map((variant) => [variant.id, mapProductVariantToEditor(variant)])
  );
  return variants.map((variant) => {
    const saved = savedById.get(variant.id);
    return saved ? mergeProductVariantEditor(variant, saved) : variant;
  });
}

/** Variantes persistidas del bundle save; `undefined` si el producto no es variable o no vienen en la respuesta. */
export function variableProductVariantsFromSavedProduct(
  product: Pick<Product, 'productStructure' | 'productVariants'>
): ProductVariantEditor[] | undefined {
  if (product.productStructure !== 'variable' || product.productVariants == null) {
    return undefined;
  }

  return mapActiveProductVariantsToEditors(product.productVariants);
}
